/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { ApiPersonDetailReviewsListPetType as PetTypeReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import type { PaginatedPersonImageListPersonalizationArm } from '@rover/rsdk/src/apiClient/latest/model/paginatedPersonImageListPersonalizationArm';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import { SitterProfileVersion } from './hooks/useRevampProfileContext';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fire when the contact button is clicked. Take into account the button in the Header and the
 * footer.
 * Read more: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/3253207320/Sitter+Profile+SSR+Phase+4+Observability
 *
 * &nbsp;
 */
export class OwnerConversionSitterProfileContactSitterEvent extends AnalyticsEvent {
  eventName = 'owner-conversion-sitter-profile-contact-sitter';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: {
    pagePosition: 'main' | 'sticky-footer' | 'sidebar';
    pageVariant?: 'revamp';
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the SittersRatesToggle radio button is clicked. For now
 * only applies to Bright Horizons.
 *
 * &nbsp;
 */
export class SitterProfileBHPriceToggleClickEvent extends AnalyticsEvent {
  eventName = 'price-toggle-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: { personId?: string }) {
    super({
      ...eventProperties,
      partner: 'brighthorizons',
      source: 'provider-profile',
    });
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the user changes the pet type filter in the reviews section.
 *
 * &nbsp;
 */
export class PetTypeReviewsFilterChanged extends AnalyticsEvent {
  eventName = 'sitter-profile-reviews-pet-type-filter-changed';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  // Adding 'all' to represent no filter selected
  constructor(eventProperties: { petType: PetTypeReview | 'all' }) {
    super(eventProperties);
  }
}

abstract class SitterGalleryEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the gallery images are loaded in the Sitter Profile page.
 *
 * &nbsp;
 */
export class SitterProfileGalleryImagesLoadedEvent extends SitterGalleryEvent {
  eventName = 'sitter-profile-gallery-images-loaded';

  constructor(eventProperties: { galleryImageCount: number; personId: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the user clicks the CTA in Sitter Profile to return to the search page.
 *
 * &nbsp;
 */
export class MemberProfileSearchCTAClickEvent extends AnalyticsEvent {
  eventName = 'member-profile-search-cta-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;
}

/**
 * # Description
 * Fires when the user clicks on the sitter photo gallery.
 * - `index`: which image was picked (0 - 4) / `inCropFix`: boolean for if in crop fix experiment
 *
 */
export class SitterProfileGalleryClickedEvent extends SitterGalleryEvent {
  eventName = 'sitter-profile-gallery-clicked';

  constructor(eventProperties: {
    inCropFix: boolean;
    index: string;
    pageVariant?: SitterProfileVersion;
  }) {
    super(eventProperties, eventProperties);
  }
}

/**
 * # Description
 * Fires when the user clicks on the next/previous arrows in sitter photo gallery lightbox (fullscreen mode).
 * - `direction`: which direction was picked (next/previous) / `inCropFix`: boolean for if in crop fix experiment
 *
 */
export class SitterProfileGalleryNavigationEvent extends SitterGalleryEvent {
  eventName = 'sitter-profile-gallery-navigation';

  constructor(eventProperties: { inCropFix: boolean; direction: string }) {
    super(eventProperties, eventProperties);
  }
}

/**
 * # Description
 * Fires when the user clicks on the sitter avatar photo.
 *
 */
export class SitterProfileAvatarClickedEvent extends SitterGalleryEvent {
  eventName = 'sitter-profile-avatar-clicked';
}

/**
 * # Description
 * Fires when the user clicks "View gallery" in the lightbox to switch to the grid view.
 *
 */
export class SitterProfileViewGalleryClickedEvent extends SitterGalleryEvent {
  eventName = 'sitter-profile-view-gallery-clicked';

  constructor(eventProperties?: { pageVariant?: SitterProfileVersion }) {
    super(eventProperties);
  }
}

/**
 * # Description
 * Fires when the user clicks the "Read more reviews" button on the sitter profile
 * to open the full reviews modal.
 *
 */
export class OwnerConversionMemberProfileReadMoreReviewsEvent extends AnalyticsEvent {
  eventName = 'owner-conversion-member-profile-read-more-reviews';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: { providerId: string; pageVariant?: SitterProfileVersion }) {
    super(eventProperties);
  }
}

/**
 * # Description
 * Fires when a collapsible section on the sitter profile is expanded or collapsed
 * (e.g. clicking "Read more" on pet care experience, qualifications, or environment).
 *
 */
export class OwnerConversionMemberProfileSectionInteractEvent extends AnalyticsEvent {
  eventName = 'owner-conversion-member-profile-section-interact';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: {
    profileSection: string;
    interactionType: 'expand' | 'collapse';
    pageVariant?: SitterProfileVersion;
  }) {
    super(eventProperties);
  }
}

/**
 * # Description
 * Fires when the user opens the cat care details modal from the cat care experience card.
 *
 */
export class CatCareDetailsModalClickEvent extends AnalyticsEvent {
  eventName = 'cat-care-details-modal-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: {
    personId?: string;
    sitterId: string;
    pageVariant?: SitterProfileVersion;
  }) {
    super(eventProperties);
  }
}

/**
 * # Description
 * Fires once per sitter-profile load whose gallery was scored for
 * personalization, in both the treatment and control arms of the A/B
 * experiment, describing the outcome: whether the order changed, the top
 * photo's score, whether the top photo matched the searcher's pets, and the
 * gallery's top scores (desc). Use `arm` to segment treatment vs. control.
 *
 * Always segment scores by `signalSource`, since each segment matches on
 * different signals and their scores are not comparable:
 * - `pet_profile`: the searcher's pets (species, breed and size).
 * - `breed_search`: breeds in their most recent search (species and breed).
 * - `cat_search`: a cat in their most recent search (species only).
 * - `no_signal`: nothing about their pets. On Cat in a Flat they are scored as
 *   `cat_search` (species only), so split this segment by brand.
 *
 * `topPhotoMatched` is null when there are no pets to match against, i.e.
 * `no_signal` outside Cat in a Flat.
 *
 * Signed-out and `no_signal` viewers log exposure on the
 * `personalized_sitter_profile_photos_expansion` experiment, not the original.
 *
 */
export class PersonalizedGalleryShown extends SitterGalleryEvent {
  eventName = 'personalized-gallery-shown';

  constructor(eventProperties: {
    personalized: boolean;
    topScore: number;
    topPhotoMatched: boolean | null;
    topScores: number[];
    arm: PaginatedPersonImageListPersonalizationArm;
    signalSource: string;
    personId: string | undefined;
  }) {
    super(eventProperties, {
      personalized: String(eventProperties.personalized),
      arm: eventProperties.arm,
      signalSource: eventProperties.signalSource,
    });
  }
}

/**
 * # Description
 * Fires once when highlights are shown on the sitter profile revamp, tracking
 * which highlight ids were displayed.
 *
 */
export class SitterProfileHighlightsShownEvent extends AnalyticsEvent {
  eventName = 'sitter-profile-highlights-shown';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.provider_profile;

  constructor(eventProperties: {
    highlightIds: string[];
    highlightCount: number;
    serviceSlug: string;
  }) {
    super(eventProperties);
  }
}
